import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Toggle, Spinner } from "@/components/peeps/ui";

const TIERS = [
  { key: "not_shared", label: "Not shared", hint: "Nobody in the group sees this calendar" },
  { key: "busy_only", label: "Busy only", hint: "Shows greyed-out busy blocks — no titles" },
  { key: "full_detail", label: "Full detail", hint: "Shows event titles & location — full exposure" },
];

export function CalendarSharingSection({ groupId }) {
  const [connected, setConnected] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [pc, vis] = await Promise.all([
        api.listPersonalCalendars().catch(() => ({ connected: false, calendars: [] })),
        api.getCalendarVisibility(groupId).catch(() => ({ enabled: true })),
      ]);
      setConnected(pc.connected);
      setCalendars(pc.calendars || []);
      setVisible(vis.enabled);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (on) => {
    setVisible(on);
    try { await api.setCalendarVisibility(groupId, on); } catch { setVisible(!on); toast("Couldn't update"); }
  };

  const setTier = async (cal, tier) => {
    setCalendars((prev) => prev.map((c) => (c.calendar_id === cal.calendar_id ? { ...c, tier } : c)));
    try { await api.setPersonalCalendar(cal.calendar_id, { tier }); }
    catch { toast("Couldn't save"); load(); }
  };

  const saveLabel = async (cal, label) => {
    if (label === cal.label) return;
    setCalendars((prev) => prev.map((c) => (c.calendar_id === cal.calendar_id ? { ...c, label } : c)));
    try { await api.setPersonalCalendar(cal.calendar_id, { label }); } catch { toast("Couldn't save label"); }
  };

  const card = { background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16 };

  return (
    <div data-testid="calendar-sharing-section">
      <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2" style={{ color: colors.muted }}>
        Share your availability
      </p>

      {loading ? (
        <div className="p-4" style={card}><Spinner /></div>
      ) : !connected ? (
        <div className="p-4" style={card}>
          <div className="flex items-center gap-3">
            <CalendarClock size={18} color={colors.muted} />
            <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>
              Connect Google Calendar above first — then choose, per calendar, how much of your schedule this group can see.
            </p>
          </div>
        </div>
      ) : (
        <div style={card}>
          <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
            {visible ? <Eye size={18} color={colors.brandSecondary} /> : <EyeOff size={18} color={colors.onSurfaceTertiary} />}
            <div className="flex-1">
              <p className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurface }}>Share my availability with this group</p>
              <p className="font-display text-[12px]" style={{ color: colors.faint }}>Off = this group sees none of your calendars, whatever their tier</p>
            </div>
            <Toggle testID="calendar-visibility-toggle" on={visible} onChange={toggleVisible} />
          </div>

          <div className="p-4 flex flex-col gap-4" style={{ opacity: visible ? 1 : 0.45, pointerEvents: visible ? "auto" : "none" }}>
            {calendars.map((c) => {
              const uid = `${c.account_id || "primary"}::${c.calendar_id}`;
              return (
              <div key={uid} data-testid={`personal-cal-${uid}`}>
                {c.account_name && (
                  <p className="font-display text-[10.5px] uppercase tracking-wide mb-1" style={{ color: colors.brandSecondary }}>{c.account_name}</p>
                )}
                <input
                  data-testid={`personal-cal-label-${uid}`}
                  defaultValue={c.label}
                  onBlur={(e) => saveLabel(c, e.target.value.trim() || c.name)}
                  className="w-full bg-transparent font-display font-bold text-[14px] mb-0.5 outline-none"
                  style={{ color: colors.onSurface }}
                />
                <p className="font-display text-[11px] mb-2" style={{ color: colors.faint }}>{c.name}{c.primary ? " · primary" : ""}</p>
                <div className="flex gap-1.5">
                  {TIERS.map((t) => {
                    const active = c.tier === t.key;
                    const danger = t.key === "full_detail";
                    return (
                      <button
                        key={t.key}
                        data-testid={`tier-${t.key}-${uid}`}
                        onClick={() => setTier(c, t.key)}
                        className="flex-1 rounded-xl py-2 px-1 transition-colors"
                        style={{
                          background: active ? (danger ? "#3A1E22" : colors.brand) : colors.surfaceTertiary,
                          border: `1px solid ${active ? (danger ? "#8A3A42" : "transparent") : colors.border}`,
                        }}
                      >
                        <span className="font-display font-medium text-[11.5px] block" style={{ color: active ? (danger ? "#F0A0A0" : colors.onBrand) : colors.onSurfaceTertiary }}>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="font-display text-[11px] mt-1.5 flex items-center gap-1" style={{ color: c.tier === "full_detail" ? "#F0A0A0" : colors.faint }}>
                  {c.tier === "full_detail" && <ShieldCheck size={11} color="#F0A0A0" />}
                  {TIERS.find((t) => t.key === c.tier)?.hint}
                </p>
              </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="font-display text-[12px] mt-2 px-1" style={{ color: colors.faint }}>
        Busy-only is the safe default. Full detail exposes every event title on that calendar — turn it on deliberately, per calendar.
      </p>
    </div>
  );
}
